import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { KeyRound, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatError(detail) {
  if (!detail) return "Errore imprevisto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputClass =
  "w-full bg-[#0A0E14] border border-white/15 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C8D7] transition-colors";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Link non valido: token mancante.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("La password deve avere almeno 8 caratteri."); return; }
    if (pwd !== confirm) { toast.error("Le due password non coincidono."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      toast.success("Password reimpostata!");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E14] tech-grid-bg flex items-center justify-center px-6 text-white">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <div className="bg-[#111827] border border-white/10 p-8 md:p-10" data-testid="reset-password-card">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="copypro3d.it" className="h-12 w-12 rounded-full object-cover" />
            <div>
              <h1 className="font-display font-bold text-xl tracking-tight">Nuova password</h1>
              <span className="font-mono-tech text-xs text-slate-500">copypro3d.it — Area Richieste</span>
            </div>
          </div>

          {!token ? (
            <div className="text-center py-4" data-testid="reset-invalid">
              <AlertTriangle size={40} className="text-[#F5871F] mx-auto" />
              <p className="text-slate-300 mt-4">Link non valido o incompleto.</p>
              <button onClick={() => nav("/admin/login")} className="mt-6 border border-[#00C8D7] text-[#00C8D7] font-bold px-6 py-2.5 hover:bg-[#00C8D7]/10 transition-colors">
                Vai al login
              </button>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 size={44} className="text-[#00C8D7] mx-auto" />
              <p className="text-slate-300 mt-4">Password aggiornata con successo.</p>
              <button data-testid="reset-goto-login" onClick={() => nav("/admin/login")} className="mt-6 bg-[#00C8D7] text-[#0A0E14] font-bold px-6 py-3 hover:bg-[#00C8D7]/85 transition-colors">
                Accedi ora
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
              <input data-testid="reset-new" type="password" className={inputClass} placeholder="Nuova password (min. 8 caratteri)" value={pwd} onChange={(e) => setPwd(e.target.value)} />
              <input data-testid="reset-confirm" type="password" className={inputClass} placeholder="Conferma nuova password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              <button data-testid="reset-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-8 py-3.5 hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-60">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
                Reimposta password
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
